import { makeAutoObservable, reaction, runInAction } from "mobx";
import type { GitWatchPath, ListDirEntry } from "@/app_store/agent_store";
import {
    PhysicalRepositoryStore,
    PhysicalRepositoryWorkspaceStore,
    ManagedRepositoryStore,
    ManagedRepositoryWorkspaceStore,
    RepositoryChangeStore,
    ChangeEntryStore,
} from "@/app_store/agent";

export interface GitSignature {
    name: string;
    email: string;
    time: number;
    offset: number;
}

export interface GitCommit {
    id: string;
    short_id: string;
    summary: string;
    message: string;
    author: GitSignature;
    committer: GitSignature;
    parent_ids: string[];
    tree_id: string;
}

export interface GitRemote {
    name: string;
    url: string;
}

export interface GitStatusEntry {
    path: string;
    flags: number;
    labels: string[];
}

export interface WorkspaceStatus {
    workspace: string;
    name: string | null;
    changes: GitStatusEntry[];
}

export type GitLoadState = "idle" | "loading" | "loaded" | "no-git" | "agent-offline" | "error";

// Mirrors ReviewSection/store.ts — keeps the old index_*/wt_* label vocabulary while
// data now comes from the generated ChangeEntry (marker, type) pair.
const ENTRY_TO_LABEL: Record<string, string> = {
    "staged:added": "index_new",
    "staged:modified": "index_modified",
    "staged:deleted": "index_deleted",
    "staged:renamed": "index_renamed",
    "staged:typechange": "index_typechange",
    "untracked:added": "wt_new",
    "unstaged:modified": "wt_modified",
    "unstaged:deleted": "wt_deleted",
    "unstaged:renamed": "wt_renamed",
    "unstaged:typechange": "wt_typechange",
    "unstaged:unreadable": "wt_unreadable",
    "conflicted:modified": "conflicted",
    "resolved:modified":   "resolved",
};

function entryToLabel(entry: ChangeEntryStore): string {
    return ENTRY_TO_LABEL[`${entry.marker}:${entry.type}`] ?? entry.marker;
}

function changeToEntry(change: RepositoryChangeStore): GitStatusEntry {
    return { path: change.path, flags: 0, labels: change.entries.map(entryToLabel) };
}


export class GitStore {
    root: any;
    loadState: GitLoadState = "idle";
    isRefreshing: boolean = false;
    workspaces: WorkspaceStatus[] = [];
    selectedBranch: string | null = null;
    private _loadId: number = 0;
    private _refreshDebounceTimer: ReturnType<typeof setTimeout> | null = null;
    private _lastAutoRefreshAt: number = 0;
    private static readonly AUTO_REFRESH_INTERVAL_MS = 60_000;
    private _watchEventDebounceTimer: ReturnType<typeof setTimeout> | null = null;
    private static readonly WATCH_EVENT_DEBOUNCE_MS = 300;

    constructor(root: any) {
        this.root = root;
        makeAutoObservable(this, {
            root: false,
            _refreshDebounceTimer: false,
            _lastAutoRefreshAt: false,
            _watchEventDebounceTimer: false,
        });

        reaction(
            () => this._selectedRepoPath,
            (relPath: string | null) => {
                if (relPath) {
                    this.provision(relPath);
                } else {
                    runInAction(() => {
                        this.loadState = "idle";
                        this.workspaces = [];
                    });
                }
            },
            { fireImmediately: true }
        );

        reaction(
            () => root.files.all.map((e: any) => e.updated_at).join(","),
            () => this._scheduleAutoRefresh()
        );

        // Restore the last selected branch when the project changes.
        reaction(
            () => root.project.selectedProject?.id ?? null,
            () => {
                const proj = root.project.selectedProject;
                runInAction(() => {
                    this.selectedBranch = (proj?.selected_branch || null) as string | null;
                });
            },
            { fireImmediately: true }
        );

        // Centralised watch emission: "/" when no project, "<rel>:<branch>" once a
        // git project is loaded. Re-emits when project, git-load state, or branch change.
        reaction(
            () => [
                root.project.selectedProject?.relative_path ?? null,
                this.loadState,
                this.effectiveBranch,
            ].join("|"),
            () => this._emitWatch(),
            { fireImmediately: true }
        );
    }

    get effectiveBranch(): string | null {
        return this.selectedBranch ?? this.currentBranch;
    }

    /** Rate-limited refresh (at most once per AUTO_REFRESH_INTERVAL_MS) driven by the
     * general files store, which updates far too often to refresh git status on every tick. */
    private _scheduleAutoRefresh() {
        if (this.loadState !== "loaded") return;
        if (this._refreshDebounceTimer !== null) clearTimeout(this._refreshDebounceTimer);
        const msSinceLast = Date.now() - this._lastAutoRefreshAt;
        const delay = Math.max(0, GitStore.AUTO_REFRESH_INTERVAL_MS - msSinceLast);
        this._refreshDebounceTimer = setTimeout(() => {
            this._refreshDebounceTimer = null;
            this._lastAutoRefreshAt = Date.now();
            this.refresh();
        }, delay);
    }

    /** Called when the backend's file watcher reports a change under the watched git
     * path (e.g. an agent session merging into the workspace branch). Short-debounced
     * (rather than the slow AUTO_REFRESH_INTERVAL_MS poll) so a merge's file writes are
     * reflected promptly instead of waiting up to a minute or a manual refresh. */
    handleWatchEvent() {
        if (this.loadState !== "loaded") return;
        if (this._watchEventDebounceTimer !== null) clearTimeout(this._watchEventDebounceTimer);
        this._watchEventDebounceTimer = setTimeout(() => {
            this._watchEventDebounceTimer = null;
            this.refresh();
        }, GitStore.WATCH_EVENT_DEBOUNCE_MS);
    }

    private get _gitContainer(): PhysicalRepositoryWorkspaceStore | ManagedRepositoryWorkspaceStore | null {
        const c = this.root.agent?.session?.container;
        if (c instanceof PhysicalRepositoryStore) return c.workspace;
        if (c instanceof ManagedRepositoryStore) return c.workspace;
        if (c instanceof PhysicalRepositoryWorkspaceStore || c instanceof ManagedRepositoryWorkspaceStore) return c;
        return null;
    }

    get changes(): GitStatusEntry[] | null {
        const c = this._gitContainer;
        if (c) return c.changes.map(changeToEntry);
        // workspaces path kept for backward compat (never populated in practice)
        if (!this.workspaces.length) return null;
        const branch = this.effectiveBranch;
        if (!branch) return null;
        const ws = this.workspaces.find(w => w.workspace === branch && w.name === null)
                ?? this.workspaces.find(w => w.workspace === branch);
        return ws?.changes ?? null;
    }

    private get _selectedRepoPath(): string | null {
        const rel = this.root.project.selectedProject?.relative_path;
        return rel != null ? rel : null;
    }

    private _emitWatch() {
        if (!this.root.agent) return;
        const project = this.root.project.selectedProject;
        if (!project) {
            this.root.agent.watch([""]);
            this.root.agent.gitWatch([]);
            return;
        }
        const rel = project.relative_path;
        if (this.loadState === "loaded" && this.effectiveBranch) {
            const gp: GitWatchPath = {
                repo_path: rel,
                workspace: this.effectiveBranch,
                worktree: null,
            };
            this.root.agent.watch([]);
            this.root.agent.gitWatch([gp]);
        } else {
            this.root.agent.watch([rel]);
            this.root.agent.gitWatch([]);
        }
    }

    setSelectedBranch(branch: string | null) {
        runInAction(() => { this.selectedBranch = branch; });
        this.root.project.setSelectedBranch(branch);
        this._emitWatch();
        // Branch switching is instant — workspaces already contain all branch data.
        // Only re-provision if branch has no workspace data yet.
        const hasData = branch === null || this.workspaces.some(w => w.workspace === branch);
        if (!hasData) {
            const path = this._selectedRepoPath;
            if (path) this.provision(path);
        }
    }

    get currentBranch(): string | null {
        const c = this._gitContainer;
        if (c) return c.name;
        return this.workspaces[0]?.workspace ?? null;
    }

    get stagedEntries(): GitStatusEntry[] {
        return this.changes?.filter(e =>
            e.labels.some(l => l.startsWith("index_"))
        ) ?? [];
    }

    get unstagedEntries(): GitStatusEntry[] {
        return this.changes?.filter(e =>
            e.labels.some(l => l.startsWith("wt_") && l !== "wt_new")
        ) ?? [];
    }

    get untrackedEntries(): GitStatusEntry[] {
        return this.changes?.filter(e =>
            e.labels.includes("wt_new")
        ) ?? [];
    }

    get totalChanges(): number {
        return (this.changes?.length ?? 0);
    }

    async provision(repoPath: string) {
        const loadId = ++this._loadId;
        const alreadyLoaded = this.loadState === "loaded";
        console.log(`[GitStore] provision start`, { repoPath, loadId, alreadyLoaded });

        runInAction(() => {
            if (alreadyLoaded) {
                this.isRefreshing = true;
            } else {
                this.loadState = "loading";
                this.workspaces = [];
            }
        });

        if (!this.root.agent.isConnected) {
            if (loadId !== this._loadId) { console.log(`[GitStore] provision stale (agent-offline check), discarding loadId=${loadId}`); return; }
            console.log(`[GitStore] provision: agent offline`);
            runInAction(() => {
                this.isRefreshing = false;
                if (!alreadyLoaded) {
                    this.loadState = "agent-offline";
                    this.workspaces = [];
                }
            });
            return;
        }

        if (loadId !== this._loadId) return;
        const hasGit = this._gitContainer !== null;
        console.log(`[GitStore] provision: container check`, { hasGit });
        runInAction(() => {
            this.isRefreshing = false;
            // Keep "no-git" even when a git container is present — "loaded" would route
            // CodeSection.fetchContent through readFile, which has no session to use.
            // The changes getter and getDiffFile now delegate to the container directly,
            // so decorations work without loadState reaching "loaded".
            this.loadState = "no-git";
            this.workspaces = [];
        });
    }

    async commit(message: string) {
        const c = this._gitContainer;
        if (!c) return;
        await c.commit(message);
    }

    async removeFile(filePath: string) {
        await this.revertFile(filePath);
    }

    async revertFile(filePath: string) {
        const c = this._gitContainer;
        if (!c) return;
        await c.revert([filePath]);
    }

    async stage(paths: string[]) {
        const c = this._gitContainer;
        if (!c) return;
        await c.stage(paths);
    }

    async unstage(paths: string[]) {
        const c = this._gitContainer;
        if (!c) return;
        await c.unstage(paths);
    }

    async createBranch(branchName: string) {
        const c = this.root.agent?.session?.container;
        if (c instanceof PhysicalRepositoryStore || c instanceof ManagedRepositoryStore) {
            await c.create_branch(branchName);
        }
    }

    async listDir(relPath: string): Promise<ListDirEntry[] | null> {
        const repoPath = this._selectedRepoPath;
        const branch = this.effectiveBranch;
        if (!repoPath || !branch) return null;
        return null;
    }

    async readFile(relPath: string): Promise<string | null> {
        // loadState never reaches "loaded" currently, so this path is not taken by
        // CodeSection.fetchContent — files are read via CodeSection's own sandbox session.
        return null;
    }

    async getDiffFile(path: string, options: { a?: string; staged?: boolean } = {}): Promise<{ path: string; original: string; modified: string }> {
        const c = this._gitContainer;
        if (!c) throw new Error("No git container");
        const data = await c.diff(path, options.a ?? null, options.staged ?? false);
        return { path, original: data.original ?? "", modified: data.modified ?? "" };
    }

    refresh() {
        const path = this._selectedRepoPath;
        console.log(`[GitStore] refresh`, { path });
        if (path) this.provision(path);
    }
}
